import sortByName from './sortByName';
import { RouteSortDirection, RouteSortKey } from '../../types/TRouteSortKey';

export interface SortableRoute {
  id?: string;
  name?: string | null;
  /** Planned departure as epoch ms; undefined when the route has no solution. */
  plannedDepartureTime?: number;
}

// Final tiebreak so the order is fully defined when names (and start times) are identical, and
// matches the CSV export's `routes.id` rule. Ids are numeric strings; compare them as numbers.
const compareIds = (a?: string, b?: string) => {
  const an = Number(a);
  const bn = Number(b);
  if (Number.isFinite(an) && Number.isFinite(bn)) return an - bn;
  return (a ?? '').localeCompare(b ?? '');
};

// `|| 0` folds the -0 that `-1 * 0` produces back to +0.
const directed = (direction: RouteSortDirection, result: number) => (
  direction === RouteSortDirection.DESC ? -result || 0 : result
);

/**
 * Comparator for the Routes page sort. Descending reverses the primary key only: the name and
 * id tiebreaks stay ascending so equal routes read the same way either way, and routes with no
 * planned start (empty / unoptimised) stay last in both directions rather than burying the
 * routes the dispatcher cares about under empty ones.
 */
const compareRoutes = (key: RouteSortKey, direction: RouteSortDirection) => <T extends SortableRoute>(a: T, b: T): number => {
  if (key === RouteSortKey.PLANNED_START_TIME) {
    const aTime = a.plannedDepartureTime;
    const bTime = b.plannedDepartureTime;
    if (aTime === undefined && bTime !== undefined) return 1;
    if (aTime !== undefined && bTime === undefined) return -1;
    if (aTime !== undefined && bTime !== undefined && aTime !== bTime) {
      return directed(direction, aTime - bTime);
    }
    return sortByName(a.name, b.name) || compareIds(a.id, b.id);
  }
  return directed(direction, sortByName(a.name, b.name)) || compareIds(a.id, b.id);
};

export default compareRoutes;
